import { Button, Chip, type ColorPaletteProp } from '@mui/joy';
import { lowerFirst } from 'lodash';
import { useTranslation } from 'react-i18next';

import { Link } from 'react-router-dom';
import { type RouterOutputs } from '@/config/trpc';
import { type ValueOf } from '~/common/types';

import { EMPTY_STRING_FALLBACK } from '~/common/constants';
import getFormattedAddress from '@/shared/utils/address';
import { routeFactories } from '@/containers/App/Router/Routes';

type CaseData = RouterOutputs['case']['repair']['getRepairCasesByTaskType']['items'][number];

type Status = ValueOf<typeof Status>;

const Status = {
  Cancelled: 'Cancelled',
  Closed: 'Closed',
  Open: 'Open',
  Pending: 'Pending',
} as const;

const STATUS_COLORS = {
  [Status.Cancelled]: 'danger',
  [Status.Closed]: 'neutral',
  [Status.Open]: 'success',
  [Status.Pending]: 'warning',
} as const satisfies {
  [Value in Status]: ColorPaletteProp;
};

type CaseCardProps = {
  caseData: CaseData;
};

const CaseCard: React.FC<CaseCardProps> = ({ caseData }) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-2 rounded-xl bg-white p-4 shadow">
      <div className="flex flex-wrap justify-between">
        <h3 className="text-lg font-semibold text-blue-900">{caseData.reference}</h3>
        <Chip className="px-4" color={STATUS_COLORS[caseData.status]} variant="soft">
          {t(lowerFirst(caseData.status))}
        </Chip>
      </div>
      <div className="text-sm font-bold">
        {t('client')}:{' '}
        {caseData.clients.map((client) => client.displayName).join(', ') || EMPTY_STRING_FALLBACK}
      </div>
      <div className="flex flex-wrap justify-between">
        <div className="text-sm">{getFormattedAddress(caseData)}</div>
        <Button
          component={Link}
          size="sm"
          target="_blank"
          variant="outlined"
          to={routeFactories.caseDetail({ caseId: caseData.id })}
        >
          {t('viewDetails')}
        </Button>
      </div>
    </div>
  );
};

export default CaseCard;
